import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Area, RegistrationApiService } from '../../../features/registration/api/RegistrationApiService';

interface CitySelectProps {
  value: string;
  governorate: string; // API-format governorate (e.g., "baghdad")
  onChange: (cityId: string) => void;
  isRTL?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  loadingText?: string;
}

const registrationApi = new RegistrationApiService();

const CitySelect: React.FC<CitySelectProps> = ({
  value,
  governorate,
  onChange,
  isRTL = false,
  disabled = false,
  error,
  placeholder = 'Select City',
  loadingText = 'Loading...',
}) => {
  const [cities, setCities] = useState<Area[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch cities when governorate changes
  useEffect(() => {
    if (!governorate) {
      setCities([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    registrationApi.getAreas(1, 500, false, governorate).then((result) => {
      if (cancelled) return;
      if (result.isSuccess && result.data) {
        setCities(result.data.filter(a => a.is_active));
      } else {
        setCities([]);
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [governorate]);

  // Reset search when value changes externally
  useEffect(() => {
    if (value) {
      const selected = cities.find(c => c.id === value);
      if (selected) {
        setSearch(isRTL ? selected.name_ar : selected.name);
      }
    } else {
      setSearch('');
    }
  }, [value, cities, isRTL]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return cities;
    const q = search.toLowerCase();
    return cities.filter(c =>
      c.name.toLowerCase().includes(q) || c.name_ar.includes(q)
    );
  }, [cities, search]);

  const handleSelect = (city: Area) => {
    onChange(city.id);
    setSearch(isRTL ? city.name_ar : city.name);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setIsOpen(true);
    if (value) {
      onChange(''); // Clear selection when user starts typing
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
    // Select all text on focus for easy replacement
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={handleInputChange}
        onFocus={handleFocus}
        disabled={disabled || !governorate}
        placeholder={loading ? loadingText : placeholder}
        className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
          error
            ? 'border-red-500'
            : 'border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20'
        } ${isRTL ? 'text-right' : 'text-left'}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      />

      {isOpen && !loading && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {filtered.map((city) => (
            <button
              key={city.id}
              type="button"
              onClick={() => handleSelect(city)}
              className={`w-full px-4 py-3 text-sm hover:bg-[#2E5D7D]/10 transition-colors ${
                city.id === value ? 'bg-[#2E5D7D]/5 font-semibold' : ''
              } ${isRTL ? 'text-right' : 'text-left'}`}
            >
              {isRTL ? city.name_ar : city.name}
            </button>
          ))}
        </div>
      )}

      {isOpen && !loading && governorate && filtered.length === 0 && search && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-sm text-gray-500 text-center">
          {isRTL ? 'لا توجد نتائج' : 'No results found'}
        </div>
      )}
    </div>
  );
};

export default CitySelect;
